import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import { TeacherRanking, useTeacherRanking } from "./useTeacherRanking.ts";

function positionOnScale(score: number, min: number, max: number): number {
  const range = max - min;
  if (range < 0.01) return 50;
  return ((score - min) / range) * 100;
}

function FinalScoreDistribution({ ranking }: { ranking: TeacherRanking }) {
  const allFinalScores = [
    ...ranking.otherTeachersFinalScores,
    ranking.finalScore,
  ];
  const lowestScore = Math.min(...allFinalScores);
  const highestScore = Math.max(...allFinalScores);
  const leftOf = (score: number) =>
    `${positionOnScale(score, lowestScore, highestScore)}%`;

  return (
    <div className={"flex flex-col gap-1"}>
      <div className={"relative h-5 mx-1.5"}>
        <div className={"absolute inset-x-0 top-1/2 h-px bg-slate-200"} />
        {ranking.otherTeachersFinalScores.map((score, index) => (
          <span
            key={index}
            title={score.toFixed(2)}
            className={
              "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-slate-400/40"
            }
            style={{ left: leftOf(score) }}
          />
        ))}
        <span
          title={`This teacher · ${ranking.finalScore.toFixed(2)}`}
          className={
            "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-slate-800 ring-2 ring-white"
          }
          style={{ left: leftOf(ranking.finalScore) }}
        />
      </div>
      <div className={"flex justify-between text-xs text-slate-400"}>
        <span>{lowestScore.toFixed(2)}</span>
        <span>{highestScore.toFixed(2)}</span>
      </div>
    </div>
  );
}

export function TeacherRankingSummary({ teacherId }: { teacherId: string }) {
  const { ranking, loading } = useTeacherRanking(teacherId);

  if (loading) {
    return <LoadingBlock sizeClassName={"h-12 w-56"} />;
  }

  if (!ranking) return null;

  return (
    <div className={"flex flex-col gap-2 min-w-64 flex-1 max-w-md"}>
      <div className={"flex items-baseline justify-between gap-3"}>
        <span className={"text-xs text-slate-500"}>
          Final score among all teachers
        </span>
        <span className={"text-xs text-slate-500 whitespace-nowrap"}>
          <span className={"text-sm font-bold text-slate-800"}>
            #{ranking.position}
          </span>{" "}
          of {ranking.teacherCount} · top {ranking.topPercentage}%
        </span>
      </div>
      <FinalScoreDistribution ranking={ranking} />
    </div>
  );
}
